import React from 'react'
import { Card, Table, Button, Row, Col } from 'react-bootstrap'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Line } from 'react-chartjs-2'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)

const BloodSugarRecord = ({
  data,
  startDate,
  endDate,
  setShowAddForm,
  onAddRecord,
}) => {
  // Process chart data
  const processChartData = () => {
    if (!startDate || !endDate || !Array.isArray(data)) {
      return {
        labels: [],
        datasets: [{ data: [] }],
      }
    }

    // 生成日期範圍
    const dateSet = new Set()
    const start = new Date(startDate)
    const end = new Date(endDate)
    const currentDate = new Date(start)

    while (currentDate <= end) {
      dateSet.add(currentDate.toISOString().split('T')[0])
      currentDate.setDate(currentDate.getDate() + 1)
    }

    const allDates = Array.from(dateSet).sort()

    // 按日期分組並計算平均值
    const dailyData = allDates.map((date) => {
      const dayRecords = data.filter((record) => {
        return (
          record && record.record_time && record.record_time.startsWith(date)
        )
      })

      if (dayRecords.length === 0) {
        return {
          date,
          record_time: `${date} 00:00:00`,
          glucose: null,
        }
      }

      // 計算當日平均值
      const avgGlucose = Math.round(
        dayRecords.reduce((sum, record) => sum + (record.glucose || 0), 0) /
          dayRecords.length
      )

      return {
        date,
        record_time: dayRecords[dayRecords.length - 1].record_time,
        glucose: avgGlucose,
      }
    })

    // 提取日期和時間
    const formatDateTime = (dateTimeStr) => {
      const date = new Date(dateTimeStr)
      const month = date.getMonth() + 1
      const day = date.getDate()
      return `${month}/${day}`
    }

    return {
      labels: allDates.map((date) => formatDateTime(`${date} 00:00:00`)),
      datasets: [
        {
          label: '血糖值',
          data: dailyData.map((item) => item.glucose),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: dailyData.map((item) =>
            item.glucose === null
              ? 'rgba(0,0,0,0)'
              : item.glucose >= 100
                ? 'rgb(255, 99, 132)'
                : 'rgb(75, 192, 192)'
          ),
          // tension: 0.3,
          pointStyle: 'circle',
          pointRadius: 6,
          pointHoverRadius: 8,
          borderWidth: 2,
          spanGaps: true,
        },
      ],
    }
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `血糖: ${context.raw || '無數據'} mg/dl`
          },
        },
      },
    },
    scales: {
      y: {
        grid: {
          color: '#CCCCCC',
          borderDash: [2, 2],
        },
        ticks: {
          stepSize: 50,
        },
        title: {
          display: true,
          text: 'mg/dl',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  }

  // Group data by date for table display
  const groupDataByDate = (records) => {
    if (!startDate || !endDate || !Array.isArray(records)) return []

    // 生成日期範圍
    const dateSet = new Set()
    const start = new Date(startDate)
    const end = new Date(endDate)
    const currentDate = new Date(start)

    while (currentDate <= end) {
      dateSet.add(currentDate.toISOString().split('T')[0])
      currentDate.setDate(currentDate.getDate() + 1)
    }

    // 轉換為數組並排序（倒序，最新的在前）
    let allDates = Array.from(dateSet).sort((a, b) => new Date(b) - new Date(a))

    // 創建日期到記錄的映射
    const recordsByDate = records.reduce((acc, record) => {
      if (!record || !record.record_time) return acc
      const date = record.record_time.split(' ')[0]
      if (!acc[date]) acc[date] = []
      acc[date].push(record)
      return acc
    }, {})

    // 為每一天創建數據結構
    return allDates.map((date) => {
      const dayRecords = recordsByDate[date] || []

      // 如果沒有記錄，添加空記錄
      if (dayRecords.length === 0) {
        dayRecords.push({
          glucose: '-',
          comment: '-',
          record_time: `${date} 00:00:00`,
        })
      } else {
        // 排序記錄，最新的在前
        dayRecords.sort(
          (a, b) => new Date(b.record_time) - new Date(a.record_time)
        )
      }

      return {
        date,
        records: dayRecords,
      }
    })
  }

  // Format time
  const formatTime = (dateTimeStr) => {
    const date = new Date(dateTimeStr)
    return `${String(date.getHours()).padStart(2, '0')}:${String(
      date.getMinutes()
    ).padStart(2, '0')}`
  }

  // Format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    return `${dateStr} ${weekdays[date.getDay()]}`
  }

  const groupedData = groupDataByDate(data || [])

  return (
    <Row className="blood-sugar-record g-3">
      {/* Chart Section */}
      <Col xs={12} lg={6}>
        <Card className="responsive-card">
          <Card.Body>
            <Card.Title className="d-flex justify-content-end align-items-center mb-4">
              <div className="ms-3 d-flex">
                <span>
                  <i
                    className="fas fa-circle me-1"
                    style={{ color: 'rgb(75, 192, 192)' }}
                  ></i>
                  血糖值
                </span>
              </div>
            </Card.Title>
            <div className="responsive-chart">
              <Line
                // key={JSON.stringify(data)}
                style={{ height: '400px' }}
                options={chartOptions}
                data={processChartData()}
              />
            </div>
          </Card.Body>
        </Card>
      </Col>

      {/* Table Section */}
      <Col xs={12} lg={6}>
        <Card className="responsive-card">
          <Card.Body>
            <div className="table-responsive">
              {groupedData.map((dayGroup) => (
                <Table
                  key={dayGroup.date}
                  hover
                  className="mb-0 border rounded-3 overflow-hidden"
                >
                  <thead>
                    <tr className="table-light">
                      <th colSpan="2">{formatDate(dayGroup.date)}</th>
                      <th className="text-end">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="px-3"
                          onClick={() => {
                            setShowAddForm(true)
                            onAddRecord?.(dayGroup.date)
                          }}
                        >
                          <i className="fa-solid fa-plus me-2"></i>
                          新增
                        </Button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayGroup.records.length === 1 &&
                    dayGroup.records[0].glucose === '-' ? (
                      <tr>
                        <td colSpan="3" className="text-center text-muted">
                          尚無血糖紀錄
                        </td>
                      </tr>
                    ) : (
                      dayGroup.records.map((record, recordIndex) => (
                        <tr key={`record-${recordIndex}`}>
                          <td>{formatTime(record.record_time)}</td>
                          <td>
                            {record.glucose === '-' ? (
                              '- mg/dl'
                            ) : (
                              <span>
                                <span
                                  className={
                                    record.glucose >= 100 ? 'text-danger' : ''
                                  }
                                >
                                  {record.glucose}
                                </span>
                                {' mg/dl'}
                              </span>
                            )}
                          </td>
                          <td>{record.comment || '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              ))}
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

export default BloodSugarRecord
